/**
 * @file document-actions.ts
 * @description Какие действия доступны в карточке документа и какое из них
 * главное (phase19.31, SC34, SC67).
 *
 * Действия — данными: ключ, подпись, значок, «опасное». Набор по статусу
 * считает чистая функция `documentActionPlan` — карточка только рисует
 * кнопки из плана, а не собирает их ветками по статусу:
 *  - черновик → главное «Выпустить»; «Удалить черновик» — опасное, последним;
 *  - выпущен → главное «Отметить подписанным», рядом «Скачать PDF», «Печать»,
 *    последним опасное «Аннулировать»;
 *  - подписан → главное «Скачать PDF», «Печать», «Аннулировать»;
 *  - аннулирован → только «Скачать PDF» и «Печать», без главного.
 * Права: выпуск и подпись — `SHOP_ISSUE_DOCUMENTS`, аннулирование и удаление
 * черновика — `SHOP_DELETE_DOCUMENTS` (crm.md, роли документов). PDF есть
 * только у выпущенного документа, у которого рендер прошёл.
 */

import { Ban, Download, FileCheck2, PenLine, Printer, Trash2, type LucideIcon } from "lucide-react";

import type { DocumentStatus } from "../../../lib/api-crm";

export type DocActionKey = "issue" | "sign" | "download" | "print" | "void" | "delete";

export interface DocActionMeta {
  label: string;
  icon: LucideIcon;
  /** Необратимое — кнопка в опасном тоне, последней, с подтверждением. */
  danger?: boolean;
}

export const DOC_ACTION_META: Record<DocActionKey, DocActionMeta> = {
  issue: { label: "Выпустить", icon: FileCheck2 },
  sign: { label: "Отметить подписанным", icon: PenLine },
  download: { label: "Скачать PDF", icon: Download },
  print: { label: "Печать", icon: Printer },
  void: { label: "Аннулировать", icon: Ban, danger: true },
  delete: { label: "Удалить черновик", icon: Trash2, danger: true },
};

export interface DocActionPlan {
  /** Главное действие (одна кнопка); `null` — главного нет. */
  primary: DocActionKey | null;
  /** Остальные видимыми кнопками; опасные — в конце. */
  secondary: DocActionKey[];
}

interface PlanInput {
  status: DocumentStatus;
  /** Файл PDF создан. */
  hasPdf: boolean;
  canIssue: boolean;
  canDelete: boolean;
}

/** Кандидаты по статусу — по порядку важности (первый доступный — главный). */
const CANDIDATES: Record<DocumentStatus, DocActionKey[]> = {
  DRAFT: ["issue", "delete"],
  ISSUED: ["sign", "download", "print", "void"],
  SIGNED: ["download", "print", "void"],
  VOIDED: ["download", "print"],
};

/** Статусы, у которых главного действия нет (аннулированный — только просмотр). */
const NO_PRIMARY: readonly DocumentStatus[] = ["VOIDED"];

function allowed(key: DocActionKey, input: PlanInput): boolean {
  switch (key) {
    case "issue":
    case "sign":
      return input.canIssue;
    case "void":
    case "delete":
      return input.canDelete;
    case "download":
    case "print":
      return input.hasPdf;
  }
}

export function documentActionPlan(input: PlanInput): DocActionPlan {
  const available = CANDIDATES[input.status].filter((key) => allowed(key, input));
  const first = available[0];
  // Опасное действие главным не бывает: оно всегда последняя кнопка в ряду.
  const primary = !NO_PRIMARY.includes(input.status) && first && !DOC_ACTION_META[first].danger ? first : null;
  const rest = available.filter((key) => key !== primary);
  return {
    primary,
    secondary: [...rest.filter((k) => !DOC_ACTION_META[k].danger), ...rest.filter((k) => DOC_ACTION_META[k].danger)],
  };
}
